import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { ROUTING_TRAMITE_PENSION_INVALIDEZ } from "@constants/url.constant";
import { InvalidezStepperService } from "../../service/invalidez-stepper.service";


const MOBILE_MAX_WIDTH = 839;
const KEYBOARD_OFFSET_FIX_CLASS = "seguridad-keyboard-offset-fix";
const KEYBOARD_OFFSET_STYLE_ID = "seguridad-keyboard-offset-fix-style";
const IONIC_SKIP_SCROLL_ASSIST = "data-ionic-skip-scroll-assist";

@Component({
  selector: "app-invalidez-seguridad",
  templateUrl: "./invalidez-seguridad.component.html",
  styleUrls: ["./invalidez-seguridad.component.scss"],
  standalone: true,
  imports: [RouterOutlet],
})

export class InvalidezSeguridadComponent implements OnInit, OnDestroy {
  private ionContentHost: HTMLElement | null = null;
  private readonly onValidacionExitosaBound =
    this.onValidacionExitosa.bind(this);
  private readonly onSeguridadInputFocusInCapture =
    this.handleSeguridadInputFocusInCapture.bind(this);

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    readonly router: Router,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly tramiteStepper: InvalidezStepperService
  ) {}

  ngOnInit(): void {
    this.tramiteStepper.setStepCurrent(1);
    globalThis.addEventListener(
      "microfront:validacionExitosa",
      this.onValidacionExitosaBound
    );
    sessionStorage.setItem("microfront_embebido", "true");
    this.setupMobileKeyboardFix(true);
  }

  private onValidacionExitosa() {
    this.router.navigate(
      [
        `/${ROUTING_TRAMITE_PENSION_INVALIDEZ.BASE_URL}/${ROUTING_TRAMITE_PENSION_INVALIDEZ.REQUEST}/${ROUTING_TRAMITE_PENSION_INVALIDEZ.DATOS_PERSONALES}`,
      ],
      {
        relativeTo: this.activatedRoute,
      }
    );
  }

  ngOnDestroy() {
    globalThis.removeEventListener(
      "microfront:validacionExitosa",
      this.onValidacionExitosaBound
    );
    this.setupMobileKeyboardFix(false);
  }

  private setupMobileKeyboardFix(enable: boolean): void {
    if (globalThis.innerWidth > MOBILE_MAX_WIDTH) {
      return;
    }

    if (enable) {
      this.injectKeyboardOffsetFixStyle();
      this.ionContentHost = this.elementRef.nativeElement.closest("ion-content");
      this.ionContentHost?.classList.add(KEYBOARD_OFFSET_FIX_CLASS);
      this.elementRef.nativeElement.addEventListener(
        "focusin",
        this.onSeguridadInputFocusInCapture,
        true
      );
      return;
    }

    this.elementRef.nativeElement.removeEventListener(
      "focusin",
      this.onSeguridadInputFocusInCapture,
      true
    );
    this.ionContentHost?.classList.remove(KEYBOARD_OFFSET_FIX_CLASS);
    this.ionContentHost = null;
    document.getElementById(KEYBOARD_OFFSET_STYLE_ID)?.remove();
  }

  private handleSeguridadInputFocusInCapture(event: FocusEvent): void {
    if (globalThis.innerWidth > MOBILE_MAX_WIDTH) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const ionInput = target.closest("ion-input");
    if (
      !ionInput ||
      !this.elementRef.nativeElement.contains(ionInput) ||
      !this.isSeguridadKeyInput(ionInput)
    ) {
      return;
    }

    const nativeInput = this.getNativeInput(ionInput, target);
    nativeInput?.setAttribute(IONIC_SKIP_SCROLL_ASSIST, "true");
  }

  private isSeguridadKeyInput(ionInput: Element): boolean {
    return (
      ionInput.closest(".digit-container") !== null ||
      ionInput.closest(".password-section") !== null ||
      ionInput.closest("ion-item.password-section") !== null
    );
  }

  private getNativeInput(
    ionInput: Element,
    target: HTMLElement
  ): HTMLInputElement | HTMLTextAreaElement | null {
    if (target.matches("input, textarea")) {
      return target as HTMLInputElement | HTMLTextAreaElement;
    }

    const nested = ionInput.shadowRoot?.querySelector("input, textarea");
    if (nested instanceof HTMLInputElement || nested instanceof HTMLTextAreaElement) {
      return nested;
    }

    return null;
  }

  private injectKeyboardOffsetFixStyle(): void {
    if (document.getElementById(KEYBOARD_OFFSET_STYLE_ID)) {
      return;
    }

    const style = document.createElement("style");
    style.id = KEYBOARD_OFFSET_STYLE_ID;
    style.textContent = `
      @media (max-width: ${MOBILE_MAX_WIDTH}px) {
        ion-content.${KEYBOARD_OFFSET_FIX_CLASS} {
          --keyboard-offset: 0px !important;
          --offset-bottom: 0px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }
}
